import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, Lock, AlertCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Logo } from '../ui/Logo';
import { Sentry } from '../../config/sentry';

export default function AuthForm({ onBack, initialMode = 'signin' }: { onBack?: () => void; initialMode?: 'signin' | 'signup' } = {}) {
  const { signInWithMagicLink, verifyOtp, signInWithPassword, signUpWithPassword, signInWithGoogle, resetPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'email' | 'verify' | 'password' | 'forgot' | 'reset-sent' | 'confirm-sent'>('email');
  const [isSignUp, setIsSignUp] = useState(initialMode === 'signup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signInWithMagicLink(email.trim());
      setStep('verify');
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'magic_link' } });
      setError(err instanceof Error ? err.message : 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyOtp(email.trim(), otp.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isSignUp) {
        await signUpWithPassword(email.trim(), password);
        setError('');
        // signUp sends a confirmation LINK, not a code. Sending the user to the
        // OTP screen asked them for six digits their email does not contain.
        setStep('confirm-sent');
      } else {
        await signInWithPassword(email.trim(), password);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    try {
      await signInWithGoogle();
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'google_signin' } });
      setError(err instanceof Error ? err.message : 'Google sign-in failed');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword(email.trim());
      setStep('reset-sent');
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'reset_password' } });
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full pl-10 pr-3 py-2 border border-[var(--input-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] bg-[var(--input-bg)] text-[var(--content-primary)] placeholder-[var(--content-tertiary)]';

  return (
    <div className="min-h-screen bg-[var(--surface-secondary)] flex items-center justify-center p-4" style={{ '--accent-primary': '#2a9d8f', '--accent-hover': '#238578', '--accent-subtle': 'rgba(42, 157, 143, 0.08)' } as React.CSSProperties}>
      <div className="bg-[var(--surface-primary)] rounded-2xl shadow-xl p-8 w-full max-w-md border border-[var(--border-primary)]">
        {/* Back to landing */}
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        )}

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <Logo size="lg" className="mb-4" />
          <p className="ps-display text-center text-[22px] leading-snug text-[var(--content-primary)]">
            {step === 'email' && (initialMode === 'signup' ? 'Create your Mozayc account' : 'Welcome back')}
            {step === 'password' && (isSignUp ? 'Create your account' : 'Welcome back')}
            {step === 'forgot' && 'Reset your password'}
            {step === 'verify' && `We sent a code to ${email}`}
            {step === 'confirm-sent' && 'Confirm your email'}
            {step === 'reset-sent' && 'Check your email'}
          </p>
        </div>

        {step === 'email' ? (
          <>
            <form onSubmit={handleSendLink} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--content-primary)] mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--content-tertiary)] w-5 h-5" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder="you@example.com"
                    autoFocus
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="bg-[var(--status-error-bg)] text-[var(--error)] px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full bg-[var(--accent-primary)] text-white py-2 px-4 rounded-lg hover:bg-[var(--accent-hover)] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Continue with email'}
              </button>
            </form>

            <button
              type="button"
              onClick={() => { setError(''); setStep('password'); }}
              className="w-full text-center text-sm text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors mt-3"
            >
              Use password instead
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[var(--border-primary)]" />
              <span className="text-xs text-[var(--content-tertiary)]">or</span>
              <div className="flex-1 h-px bg-[var(--border-primary)]" />
            </div>

            {/* Google */}
            <button
              type="button"
              onClick={handleGoogle}
              className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-[var(--input-border)] rounded-lg hover:bg-[var(--surface-tertiary)] transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span className="text-sm font-medium text-[var(--content-primary)]">Continue with Google</span>
            </button>
          </>
        ) : step === 'password' ? (
          <>
            <form onSubmit={handlePasswordAuth} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--content-primary)] mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--content-tertiary)] w-5 h-5" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder="you@example.com"
                    autoFocus={!email}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--content-primary)] mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--content-tertiary)] w-5 h-5" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 border border-[var(--input-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] bg-[var(--input-bg)] text-[var(--content-primary)] placeholder-[var(--content-tertiary)]"
                    placeholder={isSignUp ? 'Create a password (min 6 chars)' : 'Your password'}
                    autoFocus={!!email}
                    autoComplete={isSignUp ? 'new-password' : 'current-password'}
                    minLength={6}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--content-tertiary)] hover:text-[var(--content-secondary)]"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-[var(--status-error-bg)] text-[var(--error)] px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim() || password.length < 6}
                className="w-full bg-[var(--accent-primary)] text-white py-2 px-4 rounded-lg hover:bg-[var(--accent-hover)] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (isSignUp ? 'Creating account...' : 'Signing in...') : (isSignUp ? 'Create account' : 'Sign in')}
              </button>

            </form>

            {!isSignUp && (
              <button
                type="button"
                onClick={() => { setError(''); setStep('forgot'); }}
                className="w-full text-center text-sm text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors mt-2"
              >
                Forgot password?
              </button>
            )}

            <div className="flex items-center justify-between mt-4 text-sm">
              <button
                type="button"
                onClick={() => { setStep('email'); setPassword(''); setError(''); }}
                className="flex items-center gap-1 text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Use magic link
              </button>
              <button
                type="button"
                onClick={() => { setIsSignUp(!isSignUp); setError(''); }}
                className="text-[var(--accent-primary)] hover:underline"
              >
                {isSignUp ? 'Already have an account?' : 'Create account'}
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[var(--border-primary)]" />
              <span className="text-xs text-[var(--content-tertiary)]">or</span>
              <div className="flex-1 h-px bg-[var(--border-primary)]" />
            </div>

            {/* Google */}
            <button
              type="button"
              onClick={handleGoogle}
              className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-[var(--input-border)] rounded-lg hover:bg-[var(--surface-tertiary)] transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span className="text-sm font-medium text-[var(--content-primary)]">Continue with Google</span>
            </button>
          </>
        ) : step === 'forgot' ? (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--content-primary)] mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--content-tertiary)] w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="you@example.com"
                  autoFocus
                  required
                />
              </div>
              <p className="text-xs text-[var(--content-tertiary)] mt-2">
                We'll send you a link to reset your password.
              </p>
            </div>

            {error && (
              <div className="bg-[var(--status-error-bg)] text-[var(--error)] px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="w-full bg-[var(--accent-primary)] text-white py-2 px-4 rounded-lg hover:bg-[var(--accent-hover)] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending...' : 'Send reset link'}
            </button>

            <button
              type="button"
              onClick={() => { setStep('password'); setError(''); }}
              className="flex items-center gap-1 text-sm text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors mx-auto"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to sign in
            </button>
          </form>
        ) : step === 'confirm-sent' ? (
          <div className="text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-[var(--accent-subtle)] flex items-center justify-center mx-auto">
              <Mail className="w-6 h-6 text-[var(--accent-primary)]" />
            </div>
            <p className="text-sm text-[var(--content-secondary)]">
              We sent a confirmation link to <strong className="text-[var(--content-primary)]">{email}</strong>.
              Open it and you will be signed in — you can close this tab.
            </p>
            <button
              type="button"
              onClick={() => { setStep('password'); setError(''); }}
              className="flex items-center gap-1 text-sm text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors mx-auto"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
          </div>
        ) : step === 'reset-sent' ? (
          <div className="text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-[var(--accent-subtle)] flex items-center justify-center mx-auto">
              <Mail className="w-6 h-6 text-[var(--accent-primary)]" />
            </div>
            <p className="text-sm text-[var(--content-secondary)]">
              We sent a password reset link to <strong className="text-[var(--content-primary)]">{email}</strong>. Check your inbox and follow the link to set a new password.
            </p>
            <button
              type="button"
              onClick={() => { setStep('password'); setError(''); }}
              className="flex items-center gap-1 text-sm text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors mx-auto"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to sign in
            </button>
          </div>
        ) : (
          /* OTP verification step */
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--content-primary)] mb-1">
                6-digit code
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-3 border border-[var(--input-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] bg-[var(--input-bg)] text-[var(--content-primary)] text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="000000"
                autoFocus
                maxLength={6}
                required
                onFocus={(e) => e.target.setSelectionRange(0, 0)}
              />
              <p className="text-xs text-[var(--content-tertiary)] mt-2 text-center">
                Check your inbox — you can also click the link in the email directly.
              </p>
            </div>

            {error && (
              <div className="bg-[var(--status-error-bg)] text-[var(--error)] px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full bg-[var(--accent-primary)] text-white py-2 px-4 rounded-lg hover:bg-[var(--accent-hover)] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Sign in'}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => { setStep('email'); setOtp(''); setError(''); }}
                className="flex items-center gap-1 text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
              <button
                type="button"
                onClick={handleSendLink}
                disabled={loading}
                className="text-[var(--accent-primary)] hover:underline disabled:opacity-50"
              >
                Resend code
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
